import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import { PublicClientApplication } from '@azure/msal-browser';
import { checkUserExist, insertUser, getUser } from '../api/UsersContainer';
import { jwtDecode } from 'jwt-decode';

export const AuthContext = createContext({
    user: null,
    login: () => { },
    logout: () => { },
    isAuthenticated: false,
    accessToken: null,
    refreshToken: () => { },
    setAccessToken: () => { },
    role: null,
    validateToken: () => { },
    authLoading: true,
});

const msalConfig = {
    auth: {
        clientId: process.env.REACT_APP_AZURE_CLIENT_ID,
        authority: process.env.REACT_APP_AZURE_AUTHORITY_URI,
        redirectUri: window.location.origin,
    },
    scopes: ["Files.ReadWrite.All", "user.read"],
};

export const AuthProvider = ({ children }) => {
    const [userInfo, setUserInfo] = useState(null);
    const [isAuthenticatedUser, setIsAuthenticatedUser] = useState(false);
    const [msalInitialized, setMsalInitialized] = useState(false);
    const [authLoading, setAuthLoading] = useState(true);
    const msalInstanceRef = useRef(null);
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [role, setRole] = useState(null);
    const [accessToken, setAccessToken] = useState(null);

    // Initialize MSAL and restore an existing Microsoft session when available.
    useEffect(() => {
        const initializeMsal = async () => {
            try {
                const msal = new PublicClientApplication(msalConfig);
                await msal.initialize();
                msalInstanceRef.current = msal;
                setMsalInitialized(true);

                const accounts = msal.getAllAccounts();
                const account = accounts[0];

                if (account) {
                    msal.setActiveAccount(account);
                    setUserInfo(account);
                    setIsAuthenticatedUser(true);

                    const response = await msal.acquireTokenSilent({
                        scopes: msalConfig.scopes,
                        account,
                    });
                    setAccessToken(response.accessToken);
                    localStorage.setItem('accessToken', response.accessToken);
                } else {
                    setAccessToken(null);
                    setUserInfo(null);
                    setIsAuthenticatedUser(false);
                    localStorage.removeItem('accessToken');
                }
            } catch (error) {
                console.error('MSAL initialization error:', error);
                setMsalInitialized(true);
                setAccessToken(null);
                setUserInfo(null);
                setIsAuthenticatedUser(false);
                localStorage.removeItem('accessToken');
            } finally {
                setAuthLoading(false);
            }
        };

        initializeMsal();
    }, []);

    useEffect(() => {
        const checkUser = async () => {
            if (userInfo) {
                const userExists = await checkUserExist(userInfo.username);
                if (!userExists) {
                    const userData = {
                        email: userInfo.username,
                        role: 'Sender',
                        name: userInfo.name,
                    };
                    await insertUser(userData);
                    setRole('Sender');
                } else {
                    const user = await getUser(userInfo.username);
                    setRole(user.role);
                }
            }
        };

        checkUser();
    }, [userInfo]);

    const loginAzure = async () => {
        try {
            if (msalInitialized && msalInstanceRef.current && !isLoggingIn) {
                setIsLoggingIn(true);
                const response = await msalInstanceRef.current.loginPopup({
                    scopes: msalConfig.scopes, // Request scopes during login
                });
                const accounts = await msalInstanceRef.current.getAllAccounts();
                const account = response.account || accounts[0];

                if (account) {
                    msalInstanceRef.current.setActiveAccount(account);
                    setUserInfo(account);
                    setIsAuthenticatedUser(true);
                    setAccessToken(response.accessToken);
                    localStorage.setItem('accessToken', response.accessToken);
                }
            } else {
                console.error('MSAL instance is not initialized or login is already in progress');
            }
        } catch (error) {
            console.error('Login Error:', error);
        } finally {
            setIsLoggingIn(false);
        }
    };

    // Logout from Azure
    const logout = async () => {
        try {
            await msalInstanceRef.current.logout({
                account: msalInstanceRef.current.getActiveAccount(),
            });
            setAccessToken(null);
            setIsAuthenticatedUser(false);
            setUserInfo(null);
            setRole(null);
            localStorage.removeItem('accessToken');
        } catch (error) {
            console.error('Logout Error:', error);
        }
    };

    // Refresh token
    const refreshToken = useCallback(async () => {
        try {
            if (!msalInstanceRef.current) {
                return null;
            }

            const accounts = msalInstanceRef.current.getAllAccounts();
            const account = msalInstanceRef.current.getActiveAccount() || accounts[0];

            if (!account) {
                setAccessToken(null);
                setIsAuthenticatedUser(false);
                setUserInfo(null);
                localStorage.removeItem('accessToken');
                return null;
            }

            const response = await msalInstanceRef.current.acquireTokenSilent({
                scopes: msalConfig.scopes,
                account,
            });

            setAccessToken(response.accessToken);

            localStorage.setItem('accessToken', response.accessToken);
            return response;
        } catch (error) {
            console.error('Refresh Token Error:', error);
            setAccessToken(null);
            setIsAuthenticatedUser(false);
            setUserInfo(null);
            localStorage.removeItem('accessToken');
            return null;
        }
    }, []);

    // Validate and refresh token if needed
    const validateToken = useCallback(async () => {
        const token = accessToken;

        if (token) {
            try {
                const decodedToken = jwtDecode(token);
                const currentTime = Date.now() / 1000;
                const tokenExpiresIn = decodedToken.exp - currentTime;
                const bufferTime = 5 * 60; // 5 minutes buffer before the token expires

                // If the token is about to expire in less than 5 minutes, refresh it
                if (tokenExpiresIn < bufferTime) {
                    await refreshToken();
                }
            } catch (error) {
                console.error('Token validation error:', error);
                await refreshToken();
            }
        }
    }, [accessToken, refreshToken]);

    // Set an interval to validate and refresh token
    useEffect(() => {
        const intervalId = setInterval(async () => {
            await validateToken();
        }, 60 * 1000); // Run every minute to check token expiration

        return () => clearInterval(intervalId); // Cleanup on unmount
    }, [validateToken]);

    return (
        <AuthContext.Provider
            value={{
                user: userInfo,
                login: loginAzure,
                logout,
                isAuthenticated: isAuthenticatedUser,
                accessToken,
                refreshToken,
                setAccessToken,
                role,
                validateToken,
                authLoading,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
